import axios from "axios";

import type { Content, DocumentTitle, Vector } from "@virtual-brain-lib/types";

import env from "../environments";
import { infiniteQueryOptions } from "../query-client/infiniteQueryOption";
import { type QueryFunctionContext, queryOptions } from "../query-client/queryOption";

export const DOCUMENTS_PER_PAGE = 20;

export async function deleteContent({
	brainId,
	contentId,
}: {
	brainId: string;
	contentId: string;
}) {
	await axios.delete(`${env.apiUrl}/contents/${brainId}/${contentId}`);
}

export async function getContentPath(id: string) {
	const response = await axios.get<{ path: string }>(`${env.apiUrl}/contents/content-path/${id}`);

	return response.data;
}

export const getVectorData = queryOptions({
	queryKey: "vectorData",
	async queryFn({ queryKey }: QueryFunctionContext<"vectorData">) {
		const [, { contentId, vectorId }] = queryKey;

		const response = await axios.get<Vector>(`${env.apiUrl}/contents/${contentId}/${vectorId}`);

		return response.data;
	},
});

export const getContentData = queryOptions({
	queryKey: "contentData",
	async queryFn({ queryKey }: QueryFunctionContext<"contentData">) {
		const [, { contentId }] = queryKey;

		const response = await axios.get<Vector[]>(`${env.apiUrl}/contents/${contentId}/data`);

		return response.data;
	},
});

export const getContentFile = queryOptions({
	queryKey: "contentFile",
	async queryFn({ queryKey }: QueryFunctionContext<"contentFile">) {
		const [, { contentId }] = queryKey;

		const response = await axios.get<ArrayBuffer>(
			`${env.contentApiUrl}/contents/file/${contentId}`,
			{ responseType: "arraybuffer" },
		);

		return response.data;
	},
	staleTime: Infinity,
});

export const getContentParentFolderUrl = queryOptions({
	queryKey: "contentParentFolder",
	async queryFn({ queryKey }: QueryFunctionContext<"contentParentFolder">) {
		const [, { contentId }] = queryKey;

		const response = await axios.get<string>(`${env.apiUrl}/contents/${contentId}/folder`);

		return response.data;
	},
});

export async function getContentByUrl(url: string, accessToken?: string | undefined) {
	let headers;

	if (accessToken) {
		headers = {
			// eslint-disable-next-line @typescript-eslint/naming-convention
			Authorization: "Bearer " + accessToken,
		};
	}

	const result = await axios.get<Content>(env.apiUrl + "/contents", {
		headers,
		params: {
			url,
		},
	});

	return result.data;
}

export const getDocuments = infiniteQueryOptions({
	async queryFn({ queryKey, pageParam }) {
		const [, { query, brainsIds }] = queryKey;

		const response = await axios.post<{ documents: DocumentTitle[]; countTotal: number }>(
			`${env.apiUrl}/contents/documents`,
			{
				query,
				brainsIds,
				limit: DOCUMENTS_PER_PAGE,
				offset: pageParam,
			},
		);

		return response.data;
	},
	queryKey: "getDocuments",
	initialPageParam: 0,
	getNextPageParam(lastPage, allPages) {
		const loadedCount = allPages.reduce((total, page) => total + page.documents.length, 0);

		return loadedCount < lastPage.countTotal ? loadedCount : undefined;
	},
});

export const getContentsByIds = queryOptions({
	queryKey: "contentsByIds",
	async queryFn({ queryKey }: QueryFunctionContext<"contentsByIds">) {
		const [, { contentIds }] = queryKey;

		if (contentIds.length === 0) return [] as Content[];

		const response = await axios.post<{ contents: Content[] }>(
			`${env.apiUrl}/contents/by-ids`,
			{ contentIds },
		);

		return response.data.contents;
	},
});

export const getSummaryAndTitle = queryOptions({
	queryKey: "contentSummaryAndTitle",
	async queryFn({ queryKey }: QueryFunctionContext<"contentSummaryAndTitle">) {
		const [, { contentId }] = queryKey;

		const response = await axios.post<{
			summary?: string | undefined;
			title?: string | undefined;
		}>(`${env.apiUrl}/contents/summary-and-title`, { contentId });

		return response.data;
	},
});
